import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { School, Plus, Trash2, Sparkles, Send, Check, AlertCircle, Info, CheckCircle2 } from "lucide-react";
import PageShell from "@/components/PageShell";
import Button from "@/components/Button";
import FormField from "@/components/FormField";
import { api } from "@/lib/api";

const emptyTrip = () => ({
  year: "",
  destination: "",
  trip_type: "Domestic",
  students_count: "",
  students_na: false,
});

export default function RegisterSchool() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    school_name: "",
    city: "",
    coordinator_name: "",
    coordinator_mobile: "",
    coordinator_email: "",
  });
  const [hasPastTrips] = useState(true); // Mobile UI defaulted to true; keep flow
  const [trips, setTrips] = useState([emptyTrip()]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setTrip = (idx, k, v) =>
    setTrips((arr) => arr.map((t, i) => (i === idx ? { ...t, [k]: v } : t)));

  const toggleNA = (idx) => {
    setTrips((arr) =>
      arr.map((t, i) => (i === idx ? { ...t, students_na: !t.students_na } : t))
    );
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`trip_year_${idx}`];
      delete next[`trip_dest_${idx}`];
      delete next[`trip_students_${idx}`];
      return next;
    });
  };

  const addTrip = () => setTrips((arr) => [...arr, emptyTrip()]);
  const removeTrip = (idx) =>
    setTrips((arr) => (arr.length === 1 ? arr : arr.filter((_, i) => i !== idx)));

  const validate = () => {
    const e = {};
    if (form.school_name.trim().length < 2) e.school_name = "School name is required";
    if (!form.city.trim()) e.city = "City is required";
    if (form.coordinator_name.trim().length < 2) e.coordinator_name = "Coordinator name is required";
    if (form.coordinator_mobile.trim().length < 7) e.coordinator_mobile = "Enter a valid mobile number";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.coordinator_email.trim()))
      e.coordinator_email = "Enter a valid email";
    if (hasPastTrips) {
      const yr = new Date().getFullYear();
      trips.forEach((t, i) => {
        if (t.students_na) return;
        if (!t.destination.trim()) e[`trip_dest_${i}`] = "Destination required";
        const y = parseInt(t.year, 10);
        if (!t.year || isNaN(y) || y < 2000 || y > yr) e[`trip_year_${i}`] = `Year between 2000–${yr}`;
        const sc = parseInt(t.students_count || "0", 10);
        if (isNaN(sc) || sc <= 0) e[`trip_students_${i}`] = "Enter number of students";
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const getMeaningfulTrips = () => trips.filter((t) => !t.students_na);

  const performRegistration = async (forceNoTrips = false) => {
    setSubmitting(true);
    setApiError("");
    try {
      const meaningful = forceNoTrips ? [] : getMeaningfulTrips();
      await api.registerSchool({
        ...form,
        coordinator_email: form.coordinator_email.trim().toLowerCase(),
        has_past_trips: meaningful.length > 0,
        past_trips: meaningful.map((t) => ({
          year: t.year.trim(),
          destination: t.destination.trim(),
          trip_type: t.trip_type,
          students_count: parseInt(t.students_count || "0", 10),
        })),
      });
      setShowConfirm(false);
      setSuccess(true);
      toast.success("Registration submitted! Check your inbox for confirmation.");
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch {}
    } catch (err) {
      const msg = err.message || "Please try again.";
      setApiError(msg);
      setShowConfirm(false);
      toast.error(`Registration failed: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    if (getMeaningfulTrips().length === 0) {
      setShowConfirm(true);
      return;
    }
    await performRegistration(false);
  };

  const resetForm = () => {
    setForm({
      school_name: "",
      city: "",
      coordinator_name: "",
      coordinator_mobile: "",
      coordinator_email: "",
    });
    setTrips([emptyTrip()]);
    setSuccess(false);
    setApiError("");
    setErrors({});
  };

  if (success) {
    return (
      <PageShell testid="register-success-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-white rounded-3xl p-6 sm:p-10 border border-sprouts-border shadow-card text-center">
            <div className="w-20 h-20 bg-sprouts-success rounded-full flex items-center justify-center mx-auto mb-5">
              <Sparkles size={42} className="text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-sprouts-text" data-testid="register-success-title">
              Hurray! We're thrilled to have you onboard 🎉
            </h1>
            <p className="text-sm sm:text-base text-sprouts-muted mt-3 leading-relaxed">
              Thank you for registering <span className="font-extrabold text-sprouts-text">{form.school_name || "your school"}</span> for the
              Sprouts School Travel Blogging Competition 2026. We can't wait to read your students' stories.
            </p>

            <div className="mt-7 bg-sprouts-lavender border border-blue-100 rounded-2xl p-5 text-left" data-testid="next-steps-callout">
              <p className="font-black text-sprouts-text flex items-center gap-2">
                <CheckCircle2 size={20} className="text-sprouts-primary shrink-0" />
                What happens next
              </p>
              <ol className="mt-3 space-y-3 text-sm text-sprouts-text leading-relaxed">
                <li className="flex gap-2.5">
                  <span className="font-black text-sprouts-primary shrink-0">1.</span>
                  <span>
                    Our Sprouts team will review your registration and approve it shortly. You'll receive a confirmation
                    email the moment your school is approved.
                  </span>
                </li>
                <li className="flex gap-2.5">
                  <span className="font-black text-sprouts-primary shrink-0">2.</span>
                  <span>
                    Once approved, your school name will appear in the dropdown on the{" "}
                    <span className="font-extrabold">Submit Entry</span> page — and your students can start sharing
                    their travel stories as blogs, reels, videos or photo stories.
                  </span>
                </li>
                <li className="flex gap-2.5">
                  <span className="font-black text-sprouts-primary shrink-0">3.</span>
                  <span>We can't wait to see their work! 🌱</span>
                </li>
              </ol>
            </div>

            <div className="mt-5 flex items-start gap-3 bg-sprouts-blush border border-rose-200 rounded-2xl p-4 text-left">
              <Send size={20} className="text-sprouts-secondary shrink-0 mt-0.5" />
              <p className="text-sm text-sprouts-text leading-relaxed">
                <span className="font-black">Please check your inbox soon</span> — we've sent a confirmation to{" "}
                <span className="font-bold text-sprouts-secondary">{form.coordinator_email}</span>. If you don't see it,
                please check your spam or promotions folder.
              </p>
            </div>

            <div className="mt-7 grid gap-3 max-w-sm mx-auto">
              <Button onClick={() => navigate("/", { replace: true })} data-testid="register-back-home" fullWidth>
                Back to Home
              </Button>
              <Button variant="ghost" onClick={resetForm} data-testid="register-another" fullWidth>
                Register Another School
              </Button>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell testid="register-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-sprouts-lavender flex items-center justify-center shrink-0">
            <School size={28} className="text-sprouts-primary" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-sprouts-text">Register Your School</h1>
            <p className="text-sm font-bold text-sprouts-text mt-1.5" data-testid="reg-audience-note">
              This form is to be filled only by Teachers, School Administrators or Principals.
            </p>
            <p className="text-sm text-sprouts-muted mt-1.5 leading-relaxed">
              Once submitted, your registration will be reviewed by the Sprouts team. You'll be notified when approved.
            </p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl p-5 sm:p-7 border border-sprouts-border shadow-card"
        >
          <FormField
            label="School Name"
            required
            placeholder="e.g. Greenfield High School"
            value={form.school_name}
            onChange={(e) => set("school_name", e.target.value)}
            error={errors.school_name}
            data-testid="reg-school-name"
          />
          <FormField
            label="City"
            required
            placeholder="Mumbai"
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
            error={errors.city}
            data-testid="reg-city"
          />
          <FormField
            label="Coordinator Name"
            required
            placeholder="Mrs. Priya Sharma"
            value={form.coordinator_name}
            onChange={(e) => set("coordinator_name", e.target.value)}
            error={errors.coordinator_name}
            data-testid="reg-coord-name"
          />
          <div className="grid sm:grid-cols-2 gap-x-4">
            <FormField
              label="Mobile Number"
              required
              type="tel"
              placeholder="+91 9876543210"
              value={form.coordinator_mobile}
              onChange={(e) => set("coordinator_mobile", e.target.value)}
              error={errors.coordinator_mobile}
              data-testid="reg-mobile"
            />
            <FormField
              label="Email"
              required
              type="email"
              autoCapitalize="none"
              placeholder="coordinator@school.edu"
              value={form.coordinator_email}
              onChange={(e) => set("coordinator_email", e.target.value)}
              error={errors.coordinator_email}
              data-testid="reg-email"
            />
          </div>

          <h3 className="text-[15px] font-extrabold text-sprouts-text mt-4 mb-1">
            A little about your school's travel journey
          </h3>
          <p className="text-xs text-sprouts-muted leading-relaxed mb-4">
            We'd love to hear about any educational or leisure trips your school has organised so far. This also helps
            us consider your school for nomination in our upcoming{" "}
            <span className="font-black text-sprouts-secondary">Experiential Learning Awards</span> in association with{" "}
            <span className="font-black">TOI NIE</span>, and gives our judges helpful context while evaluating your
            students' entries.
          </p>

          {trips.map((t, idx) => (
            <div
              key={idx}
              className="bg-sprouts-bg rounded-xl p-4 mb-3 border border-sprouts-border"
              data-testid={`trip-card-${idx}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-extrabold text-sm text-sprouts-text">Trip #{idx + 1}</span>
                {trips.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTrip(idx)}
                    data-testid={`trip-remove-${idx}`}
                    className="p-1 rounded hover:bg-sprouts-blush text-sprouts-secondary"
                    aria-label="Remove trip"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <FormField
                  label="Year"
                  inputMode="numeric"
                  placeholder="2024"
                  value={t.year}
                  onChange={(e) =>
                    setTrip(idx, "year", e.target.value.replace(/[^0-9]/g, "").slice(0, 4))
                  }
                  error={errors[`trip_year_${idx}`]}
                  data-testid={`trip-year-${idx}`}
                />
                <FormField
                  label="Destination"
                  className="col-span-2"
                  placeholder="e.g. Jaipur, Singapore"
                  value={t.destination}
                  onChange={(e) => setTrip(idx, "destination", e.target.value)}
                  error={errors[`trip_dest_${idx}`]}
                  data-testid={`trip-dest-${idx}`}
                />
              </div>
              <p className="text-[13px] font-bold text-sprouts-text mb-1.5">Trip Type</p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {["Domestic", "International"].map((tp) => (
                  <button
                    key={tp}
                    type="button"
                    onClick={() => setTrip(idx, "trip_type", tp)}
                    data-testid={`trip-type-${idx}-${tp.toLowerCase()}`}
                    className={`py-3 rounded-lg text-xs font-extrabold border transition-colors ${
                      t.trip_type === tp
                        ? "bg-sprouts-primary text-white border-sprouts-primary"
                        : "bg-white text-sprouts-text border-sprouts-border hover:bg-slate-50"
                    }`}
                  >
                    {tp}
                  </button>
                ))}
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <FormField
                    label="Number of Students who went"
                    inputMode="numeric"
                    placeholder={t.students_na ? "NA" : "e.g. 45"}
                    value={t.students_na ? "" : t.students_count}
                    disabled={t.students_na}
                    onChange={(e) =>
                      setTrip(idx, "students_count", e.target.value.replace(/[^0-9]/g, ""))
                    }
                    error={errors[`trip_students_${idx}`]}
                    data-testid={`trip-students-${idx}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => toggleNA(idx)}
                  data-testid={`trip-students-na-${idx}`}
                  className={`mb-4 inline-flex items-center gap-1.5 px-3 py-3 rounded-lg border text-xs font-extrabold ${
                    t.students_na
                      ? "bg-sprouts-muted text-white border-sprouts-muted"
                      : "bg-white text-sprouts-muted border-sprouts-border"
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded border ${
                      t.students_na ? "bg-white border-white" : "border-sprouts-border"
                    }`}
                  >
                    {t.students_na && <Check size={14} className="text-sprouts-muted" />}
                  </span>
                  NA
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addTrip}
            data-testid="reg-add-trip"
            className="w-full mt-1 inline-flex items-center justify-center gap-2 py-3 rounded-full border-2 border-dashed border-sprouts-primary text-sprouts-primary font-extrabold text-sm hover:bg-sprouts-lavender"
          >
            <Plus size={18} />
            Add Another Trip
          </button>

          {apiError && (
            <div
              className="mt-5 flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-xl p-4"
              data-testid="reg-api-error"
            >
              <AlertCircle size={18} className="text-sprouts-secondary shrink-0 mt-0.5" />
              <p className="text-sm font-bold text-sprouts-secondary">{apiError}</p>
            </div>
          )}

          <div className="mt-6">
            <Button type="submit" loading={submitting} data-testid="reg-submit" fullWidth>
              {submitting ? "Submitting..." : "Register School"}
            </Button>
          </div>
        </form>
      </div>

      {/* Confirm "no trips" modal */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/55 flex items-center justify-center p-4"
          data-testid="confirm-no-travel-modal"
          onClick={() => setShowConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-card"
          >
            <div className="w-16 h-16 rounded-full bg-sprouts-lavender flex items-center justify-center mx-auto mb-4">
              <Info size={32} className="text-sprouts-primary" />
            </div>
            <h3 className="text-xl font-black text-sprouts-text text-center mb-2">Just checking</h3>
            <p className="text-sm text-sprouts-muted text-center leading-relaxed mb-6">
              Has your school organised any <span className="font-black">domestic or international</span> tours in the
              past?
              <br />
              <br />
              Even <span className="font-black">day trips</span> or short educational excursions can be mentioned — no
              detailed information needed, just the destination/place is enough.
              <br />
              <br />
              And if your school hasn't conducted tours yet, that's absolutely fine too — we'd still be delighted to
              have your school participate.
            </p>
            <div className="grid gap-2">
              <Button onClick={() => setShowConfirm(false)} data-testid="confirm-add-trip" fullWidth>
                Let me add a trip
              </Button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => performRegistration(true)}
                data-testid="confirm-register-anyway"
                className="w-full py-3 rounded-full border border-sprouts-border text-sm font-bold text-sprouts-muted hover:bg-slate-50 disabled:opacity-60"
              >
                {submitting ? "Registering..." : "Register our school anyway"}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
